import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConsentPurpose, ConsentSnapshot } from 'src/app/core/consent/consent.model';
import { ConsentService } from 'src/app/core/consent/consent.service';
import { SnackBarService } from 'src/app/core/services/snackbar.service';

@Component({
  selector: 'ctv-consent-banner',
  templateUrl: './consent-banner.component.html',
  styleUrls: ['./consent-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ConsentBannerComponent implements OnInit, OnDestroy {
  open = false;
  saving = false;
  manage = false;
  snapshot: ConsentSnapshot | null = null;
  choices: Record<string, boolean> = {};

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly consentService: ConsentService,
    private readonly snackBarService: SnackBarService,
  ) {}

  ngOnInit(): void {
    this.consentService.snapshot$.pipe(takeUntil(this.destroy$)).subscribe((snapshot) => {
      this.snapshot = snapshot;
      this.choices = this.choicesFrom(snapshot);
    });
    this.consentService.bannerOpen$.pipe(takeUntil(this.destroy$)).subscribe((open) => {
      this.open = open;
      if (open) {
        this.choices = this.choicesFrom(this.snapshot || this.consentService.snapshot$.value);
      } else {
        this.manage = false;
      }
    });
    this.consentService.saving$.pipe(takeUntil(this.destroy$)).subscribe((saving) => {
      this.saving = saving;
    });
    this.consentService.saveError$.pipe(takeUntil(this.destroy$)).subscribe((message) => {
      if (message) {
        this.snackBarService.error(message);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get purposes(): ConsentPurpose[] {
    return this.snapshot?.purposes || [];
  }

  acceptAll(): void {
    this.consentService.save(this.choicesFor(true));
  }

  rejectOptional(): void {
    this.consentService.save(this.choicesFor(false));
  }

  saveChoices(): void {
    this.consentService.save({ ...this.choices });
  }

  private choicesFrom(snapshot: ConsentSnapshot): Record<string, boolean> {
    const choices: Record<string, boolean> = {};
    snapshot.purposes.forEach((purpose) => {
      choices[purpose.key] = !!snapshot.choices?.[purpose.key];
    });
    return choices;
  }

  private choicesFor(granted: boolean): Record<string, boolean> {
    const choices: Record<string, boolean> = {};
    this.purposes.forEach((purpose) => {
      choices[purpose.key] = granted;
    });
    return choices;
  }
}
